type StandDownRecord = {
	dismissalTimestamps: number[];
};

export interface StandDownManagerOptions {
	/** The content/document identifier used to scope stand-down records. */
	contentId: string;
	/** Maximum dismissals allowed within the time window. Default: 1 */
	maxDismissalsInWindow?: number;
	/** localStorage key prefix. Default: 'ai-quick-prompt-standdown' */
	storageKeyPrefix?: string;
	/** Time window in milliseconds. Default: 86400000 (24 hours) */
	windowMs?: number;
}

const DEFAULT_STORAGE_KEY_PREFIX = 'ai-quick-prompt-standdown';
const DEFAULT_MAX_DISMISSALS_IN_WINDOW = 1;
const DEFAULT_WINDOW_MS = 24 * 60 * 60 * 1000; // 24 hours

export class StandDownManager {
	private contentId: string;
	private storageKeyPrefix: string;
	private maxDismissalsInWindow: number;
	private windowMs: number;

	constructor(options: StandDownManagerOptions) {
		this.contentId = options.contentId;
		this.storageKeyPrefix = options.storageKeyPrefix ?? DEFAULT_STORAGE_KEY_PREFIX;
		this.maxDismissalsInWindow = options.maxDismissalsInWindow ?? DEFAULT_MAX_DISMISSALS_IN_WINDOW;
		this.windowMs = options.windowMs ?? DEFAULT_WINDOW_MS;
	}

	shouldStandDown(): boolean {
		const record = this.pruneExpired(this.readRecord());
		this.writeRecord(record);

		if (record.dismissalTimestamps.length >= this.maxDismissalsInWindow) {
			return true;
		}
		return false;
	}

	recordDismissal(): void {
		const record = this.readRecord();
		record.dismissalTimestamps.push(Date.now());
		this.writeRecord(record);
	}

	reset(): void {
		try {
			localStorage.removeItem(this.getStorageKey());
		} catch {
			// Silently ignore storage errors
		}
	}

	private getStorageKey(): string {
		return `${this.storageKeyPrefix}-${this.contentId}`;
	}

	private readRecord(): StandDownRecord {
		try {
			const raw = localStorage.getItem(this.getStorageKey());
			if (!raw) {
				return { dismissalTimestamps: [] };
			}
			return JSON.parse(raw) as StandDownRecord;
		} catch {
			return { dismissalTimestamps: [] };
		}
	}

	private writeRecord(record: StandDownRecord): void {
		try {
			localStorage.setItem(this.getStorageKey(), JSON.stringify(record));
		} catch {
			// Silently ignore storage errors (e.g., quota exceeded)
		}
	}

	private pruneExpired(record: StandDownRecord): StandDownRecord {
		const cutoff = Date.now() - this.windowMs;
		return {
			dismissalTimestamps: record.dismissalTimestamps.filter((ts) => ts > cutoff),
		};
	}
}
